import { Link } from 'react-router-dom';
import { formatPercent, formatMs, formatNumber, getHealthColor } from '../../utils/formatters';

/**
 * ExecutiveSummary — 6 clean KPI cards in a grid.
 * Decision in 10 seconds: Health Score, Crash-Free Users, Crash-Free Sessions, ANR Rate, Startup P90, Active Users
 */

function KPIMetricCard({ title, value, format, trend, trendLabel, status, linkTo, source }) {
  const getStatusColor = (s) => {
    if (!s) return '';
    if (s === 'good' || s === 'healthy') return 'border-l-emerald-500';
    if (s === 'warning' || s === 'monitor') return 'border-l-amber-500';
    return 'border-l-red-500';
  };

  const getTrendIcon = (t) => {
    if (!t || t === 0) return null;
    if (t > 0) return <span className="text-emerald-400 text-xs font-medium">↑ {Math.abs(t).toFixed(2)}</span>;
    return <span className="text-red-400 text-xs font-medium">↓ {Math.abs(t).toFixed(2)}</span>;
  };

  const content = (
    <div className={`bg-surface-700/60 rounded-xl p-4 border-l-4 ${getStatusColor(status)} hover:bg-surface-700 transition-all group cursor-pointer`}>
      <div className="flex items-start justify-between mb-2">
        <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">{title}</p>
        {source && <span className="text-[10px] text-gray-600 bg-surface-600/50 px-1.5 py-0.5 rounded">{source}</span>}
      </div>
      <div className="flex items-end gap-2">
        <p className="text-2xl font-bold text-gray-100">{format ? format(value) : value}</p>
        <div className="flex flex-col items-start mb-1">
          {getTrendIcon(trend)}
          {trendLabel && <span className="text-[10px] text-gray-600">{trendLabel}</span>}
        </div>
      </div>
      {linkTo && (
        <svg className="w-3.5 h-3.5 text-gray-600 group-hover:text-brand-400 absolute top-3 right-3 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      )}
    </div>
  );

  if (linkTo) {
    return <Link to={linkTo} className="relative block">{content}</Link>;
  }
  return <div className="relative">{content}</div>;
}

export default function ExecutiveSummary({ summary, platformRisk, platformSummary }) {
  if (!summary) return null;

  const overall = summary.overall || {};
  const prev = summary.previousWeek || {};
  const playConsole = summary.playConsole;
  const risk = summary.latestReleaseRisk || platformRisk;

  // Compute trends (current - previous)
  const crashFreeTrend = overall.avg_crash_free && prev.avg_crash_free
    ? parseFloat(overall.avg_crash_free) - parseFloat(prev.avg_crash_free)
    : null;
  const anrTrend = overall.avg_anr_rate && prev.avg_anr_rate
    ? parseFloat(prev.avg_anr_rate) - parseFloat(overall.avg_anr_rate) // inverted: lower is better
    : null;
  const startupTrend = overall.avg_startup_p90 && prev.avg_startup_p90
    ? parseFloat(prev.avg_startup_p90) - parseFloat(overall.avg_startup_p90) // inverted: lower is better
    : null;

  const healthScore = risk?.releaseRiskScore || parseFloat(overall.avg_health_score) || 0;
  const crashFreeUsers = parseFloat(overall.avg_crash_free) || 0;
  const crashFreeSessions = parseFloat(overall.crash_free_sessions_pct) || 0;
  const anrRate = parseFloat(overall.avg_anr_rate) || 0;
  const startupP90 = parseInt(overall.avg_startup_p90) || 0;
  const activeUsers = parseInt(overall.total_users_7d) || 0;

  // Determine status thresholds
  const getHealthStatus = (score) => score >= 90 ? 'good' : score >= 75 ? 'warning' : 'critical';
  const getCrashStatus = (pct) => pct >= 99 ? 'good' : pct >= 95 ? 'warning' : 'critical';
  const getAnrStatus = (rate) => rate <= 0.1 ? 'good' : rate <= 0.47 ? 'warning' : 'critical';

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      <KPIMetricCard
        title="Health Score"
        value={healthScore}
        format={(v) => Math.round(v)}
        status={getHealthStatus(healthScore)}
        linkTo="/metric/health-score"
        source="Combined"
      />
      <KPIMetricCard
        title="Crash-Free Users"
        value={crashFreeUsers}
        format={(v) => `${v.toFixed(2)}%`}
        trend={crashFreeTrend}
        trendLabel="vs last week"
        status={getCrashStatus(crashFreeUsers)}
        linkTo="/metric/crash-free"
        source="Crashlytics"
      />
      <KPIMetricCard
        title="Crash-Free Sessions"
        value={crashFreeSessions || crashFreeUsers}
        format={(v) => `${v.toFixed(2)}%`}
        status={getCrashStatus(crashFreeSessions || crashFreeUsers)}
        linkTo="/metric/crash-free"
        source="Play Console"
      />
      <KPIMetricCard
        title="ANR Rate"
        value={anrRate}
        format={(v) => `${v.toFixed(3)}%`}
        trend={anrTrend}
        trendLabel="vs last week"
        status={getAnrStatus(anrRate)}
        linkTo="/metric/anr-rate"
        source="Crashlytics"
      />
      <KPIMetricCard
        title="Startup P90"
        value={startupP90}
        format={(v) => formatMs(v)}
        trend={startupTrend}
        trendLabel="vs last week"
        status={startupP90 < 3000 ? 'good' : startupP90 < 5000 ? 'warning' : 'critical'}
        linkTo="/metric/cold-start"
        source="Firebase"
      />
      <KPIMetricCard
        title="Active Users"
        value={activeUsers}
        format={(v) => formatNumber(v)}
        source="7d"
      />
    </div>
  );
}
